import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useInView, useReducedMotion } from 'framer-motion';
import { useTheme } from '../hooks/useTheme';
import { isAuthenticated, API_BASE } from '../services/api';
import { LANDING_CONTENT as C } from '../components/landing/landingContent';

import {
  CAP_CARD_ICONS,
  CapSyncIcon,
} from '../components/landing/capIcons';
import '../components/landing/landing.css';

const TRUSTED_LOGOS = [
  { name: 'Partner 1', src: '/1.webp', h: 36 },
  { name: 'Partner 2', src: '/2.webp', h: 36 },
  { name: 'Partner 3', src: '/3.png', h: 36 },
  { name: 'Partner 4', src: '/4.png', h: 36 },
];

function resetToHome() {
  sessionStorage.setItem('app_view', 'home');
  sessionStorage.removeItem('app_active_project');
}

function GetStartedLink({ className, children, onClick, ...props }) {
  const authed = isAuthenticated();
  const to = authed ? '/app' : '/login';

  const handleClick = (e) => {
    resetToHome();
    onClick?.(e);
  };

  return (
    <Link
      to={to}
      state={{ resetHome: true }}
      className={className}
      onClick={handleClick}
      {...props}
    >
      {children}
    </Link>
  );
}

function LoginLink({ className, children, onClick, ...props }) {
  const handleClick = (e) => {
    resetToHome();
    onClick?.(e);
  };

  return (
    <Link
      to="/login"
      state={{ resetHome: true }}
      className={className}
      onClick={handleClick}
      {...props}
    >
      {children}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Motion helpers
// ---------------------------------------------------------------------------

function FadeUp({ children, className = '', delay = 0, y = 24, once, ...props }) {
  const reduce = useReducedMotion();
  if (reduce) {
    return <div className={className} {...props}>{children}</div>;
  }
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: once !== false, margin: '-60px' }}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      {...props}
    >
      {children}
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

const LP_NAV_SCROLL_SECTIONS = [
  { sectionId: 'how-it-works', navId: 'how-it-works' },
  { sectionId: 'workflow', navId: 'workflow' },
  { sectionId: 'workspace', navId: 'workspace' },
  { sectionId: 'templates', navId: 'templates' },
  { sectionId: 'blog', navId: 'blog' },
  { sectionId: 'pricing', navId: 'pricing' },
];

function useActiveNavSection(sections, offset = 120) {
  const [active, setActive] = useState('');

  useEffect(() => {
    const onScroll = () => {
      let current = '';
      for (const { sectionId, navId } of sections) {
        const el = document.getElementById(sectionId);
        if (el && el.getBoundingClientRect().top <= offset) {
          current = navId;
        }
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [sections, offset]);

  return active;
}

function useActiveLoop(ref, interval, enabled = true, margin = '-80px') {
  const [tick, setTick] = useState(0);
  const inView = useInView(ref, { once: false, margin });

  useEffect(() => {
    if (!enabled || !inView) return undefined;
    const t = setInterval(() => setTick(n => n + 1), interval);
    return () => clearInterval(t);
  }, [inView, interval, enabled]);

  return { inView, tick };
}

// ---------------------------------------------------------------------------
// Nav
// ---------------------------------------------------------------------------

function BrandLogo() {
  return (
    <span className="lp-brand-logo" aria-hidden>
      <span className="lp-brand-logo__surface">
        <span className="lp-brand-logo__shape lp-brand-logo__shape--a" />
        <span className="lp-brand-logo__shape lp-brand-logo__shape--b" />
        <span className="lp-brand-logo__shape lp-brand-logo__shape--c" />
      </span>
    </span>
  );
}

function LandingNav({ onScrollTo }) {
  const { mode, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const active = useActiveNavSection(LP_NAV_SCROLL_SECTIONS);

  // pill indicator — mirrors AppShell nav indicator logic
  const navRef = useRef(null);
  const tabRefs = useRef({});
  const [indicator, setIndicator] = useState({ left: 0, width: 0, opacity: 0 });

  const updateIndicator = useCallback(() => {
    const btn = tabRefs.current[active];
    const nav = navRef.current;
    if (!btn || !nav) { setIndicator(s => ({ ...s, opacity: 0 })); return; }
    const navRect = nav.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    if (btnRect.width <= 0) return;
    setIndicator({ left: btnRect.left - navRect.left, width: btnRect.width, opacity: 1 });
  }, [active]);

  useLayoutEffect(() => {
    updateIndicator();
    const raf = requestAnimationFrame(() => { updateIndicator(); requestAnimationFrame(updateIndicator); });
    const nav = navRef.current;
    let ro;
    if (nav && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateIndicator);
      ro.observe(nav);
    }
    return () => { cancelAnimationFrame(raf); ro?.disconnect(); };
  }, [updateIndicator]);

  useEffect(() => {
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [updateIndicator]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const go = (scrollTo) => {
    setMenuOpen(false);
    if (scrollTo === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      onScrollTo(scrollTo);
    }
  };
  const indicatorReady = indicator.opacity && indicator.width > 0;

  return (
    <header className={`lp-nav${scrolled ? ' lp-nav--scrolled' : ''}`}>

      {/* Brand */}
      <Link
        to="/"
        className="lp-nav__brand"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <BrandLogo />
        <span className="lp-nav__brand-text">
          <span className="lp-nav__name">{C.brand.name}</span>
          <span className="lp-nav__tagline">{C.brand.tagline}</span>
        </span>
      </Link>

      {/* Nav pill */}
      <div className="lp-nav__pill-wrap">
        <nav
          ref={navRef}
          className={`lp-nav__pill${indicatorReady ? ' lp-nav__pill--ready' : ''}`}
          aria-label="Primary"
        >
          <span
            className="lp-nav__indicator"
            aria-hidden
            style={{
              transform: `translate3d(${indicator.left}px, 0, 0)`,
              width: indicator.width,
              opacity: indicator.opacity,
            }}
          />
          {C.nav.map(item => (
            <button
              key={item.id}
              type="button"
              ref={el => { tabRefs.current[item.id] = el; }}
              className={`lp-nav__tab${active === item.id ? ' lp-nav__tab--active' : ''}`}
              onClick={() => go(item.scrollTo)}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Actions capsule */}
      <div className="lp-nav__actions">
        <LoginLink className="lp-nav__login">Log In</LoginLink>
        <GetStartedLink className="lp-nav__cta">Get Started Free</GetStartedLink>
        <button
          type="button"
          className="lp-nav__menu"
          onClick={() => setMenuOpen(o => !o)}
          aria-expanded={menuOpen}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        >
          {menuOpen ? '✕' : '☰'}
        </button>
      </div>

      {/* Theme toggle — true right edge */}
      <div className="lp-nav__theme">
        <button
          type="button"
          className="lp-nav__theme-toggle"
          onClick={toggle}
          title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label="Toggle theme"
        >
          {mode === 'dark' ? '☀️' : '🌙'}
        </button>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="lp-nav__mobile"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="lp-container">
              {C.nav.map(item => (
                <button key={item.id} type="button" onClick={() => go(item.scrollTo)}>{item.label}</button>
              ))}
              <div className="lp-nav__mobile-actions">
                <LoginLink onClick={() => setMenuOpen(false)}>Log In</LoginLink>
                <GetStartedLink className="lp-nav__cta" onClick={() => setMenuOpen(false)}>
                  Get Started Free
                </GetStartedLink>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

const HERO_STEP_MS = 650;
const HERO_OUTPUT_MS = 1400;
const HERO_COMPLETE_MS = 1000;

function HeroExecCheck() {
  return (
    <svg className="lp-hero-exec__check" viewBox="0 0 12 12" aria-hidden>
      <path d="M2 6l3 3 5-5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function HeroExecutionViz({ projects }) {
  const reduce = useReducedMotion();
  const [cycle, setCycle] = useState({ projectIdx: 0, stepIdx: 0, phase: 'running' });

  useEffect(() => {
    if (reduce) return undefined;

    let timer;
    let mounted = true;

    const advance = (projectIdx, stepIdx, phase) => {
      if (!mounted) return;
      const project = projects[projectIdx];
      const lastStep = project.steps.length - 1;

      if (phase === 'running') {
        if (stepIdx < lastStep) {
          timer = setTimeout(() => {
            const next = stepIdx + 1;
            setCycle({ projectIdx, stepIdx: next, phase: 'running' });
            advance(projectIdx, next, 'running');
          }, HERO_STEP_MS);
        } else {
          timer = setTimeout(() => {
            setCycle({ projectIdx, stepIdx, phase: 'output' });
            advance(projectIdx, stepIdx, 'output');
          }, HERO_STEP_MS);
        }
      } else if (phase === 'output') {
        timer = setTimeout(() => {
          setCycle({ projectIdx, stepIdx, phase: 'complete' });
          advance(projectIdx, stepIdx, 'complete');
        }, HERO_OUTPUT_MS);
      } else {
        timer = setTimeout(() => {
          const nextProject = (projectIdx + 1) % projects.length;
          setCycle({ projectIdx: nextProject, stepIdx: 0, phase: 'running' });
          advance(nextProject, 0, 'running');
        }, HERO_COMPLETE_MS);
      }
    };

    timer = setTimeout(() => advance(0, 0, 'running'), HERO_STEP_MS);

    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [reduce, projects]);

  const staticCycle = { projectIdx: 0, stepIdx: projects[0].steps.length - 1, phase: 'output' };
  const { projectIdx, stepIdx, phase } = reduce ? staticCycle : cycle;
  const project = projects[projectIdx];
  const visibleSteps = project.steps.slice(0, stepIdx + 1);

  return (
    <div className="lp-hero-exec" aria-live="polite" aria-label="AI build execution">
      <div className="lp-hero-exec__block">
        <span className="lp-hero-exec__label">User Request</span>
        <AnimatePresence mode="wait">
          <motion.div
            key={projectIdx}
            className="lp-hero-exec__request"
            initial={reduce ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0, y: -6 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="lp-hero-exec__request-dot" aria-hidden />
            <span className="lp-hero-exec__request-text">{project.request}</span>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="lp-hero-exec__block">
        <span className="lp-hero-exec__label">AI Processing</span>
        <ul className="lp-hero-exec__stream">
          {visibleSteps.map((step, i) => {
            const done = phase !== 'running' || i < stepIdx;
            const active = phase === 'running' && i === stepIdx;
            return (
              <motion.li
                key={`${projectIdx}-${step}`}
                className={[
                  'lp-hero-exec__stream-item',
                  done && 'lp-hero-exec__stream-item--done',
                  active && 'lp-hero-exec__stream-item--active',
                ].filter(Boolean).join(' ')}
                initial={reduce ? false : { opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                <span className="lp-hero-exec__stream-status" aria-hidden>
                  {done ? <HeroExecCheck /> : <span className="lp-hero-exec__stream-pulse" />}
                </span>
                <span className="lp-hero-exec__stream-text">{step}</span>
              </motion.li>
            );
          })}
        </ul>
      </div>

      <div className="lp-hero-exec__block">
        <span className="lp-hero-exec__label">Output Status</span>
        <div className="lp-hero-exec__output-wrap">
          <AnimatePresence mode="wait">
            {phase === 'complete' ? (
              <motion.div
                key={`complete-${projectIdx}`}
                className="lp-hero-exec__complete"
                initial={reduce ? false : { opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={reduce ? undefined : { opacity: 0 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              >
                <HeroExecCheck />
                <span>Build Complete</span>
              </motion.div>
            ) : phase === 'output' ? (
              <motion.div
                key={`output-${projectIdx}`}
                className="lp-hero-exec__output"
                initial={reduce ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? undefined : { opacity: 0, y: -4 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="lp-hero-exec__output-title">{project.output.title}</p>
                <ul className="lp-hero-exec__output-list">
                  {project.output.items.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </motion.div>
            ) : (
              <motion.div
                key="pending"
                className="lp-hero-exec__pending"
                initial={false}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <span className="lp-hero-exec__pending-line" />
                <span className="lp-hero-exec__pending-line lp-hero-exec__pending-line--short" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function LandingHero({ onSeeHowItWorks }) {
  const { hero } = C;
  const [showVideo, setShowVideo] = useState(false);

  return (
    <section className="lp-hero">
      <div className="lp-hero__grid">
        <FadeUp className="lp-hero__copy" once>
          <h1 className="lp-hero__headline">
            {hero.headline.map((line, i) => (
              <span
                key={line}
                className={i === hero.highlightLine ? 'lp-hero__headline-accent' : undefined}
              >
                {line}
                {i < hero.headline.length - 1 && <br />}
              </span>
            ))}
          </h1>
          <p className="lp-hero__sub">{hero.subheadline}</p>
          <div className="lp-hero__ctas">
            <GetStartedLink className="lp-btn lp-btn--primary lp-btn--lg">{hero.ctaPrimary}</GetStartedLink>
            <button type="button" className="lp-btn lp-btn--secondary lp-btn--lg" onClick={onSeeHowItWorks}>
              {hero.ctaSecondary}
            </button>
          </div>
          <div className="lp-hero__trust">
            {hero.trust.map(item => (
              <span key={item} className="lp-hero__trust-item">{item}</span>
            ))}
          </div>
        </FadeUp>

        <FadeUp className="lp-hero__visual" delay={0.1} style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setShowVideo(true)}
            aria-label="Play demo video"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 99,
              width: 68,
              height: 68,
              borderRadius: '50%',
              background: 'rgba(99, 102, 241, 0.92)',
              border: '2px solid rgba(255, 255, 255, 0.35)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              outline: 'none',
              boxShadow: '0 8px 32px rgba(99,102,241,0.5), 0 0 0 6px rgba(99,102,241,0.15)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.12)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(99,102,241,0.65), 0 0 0 8px rgba(99,102,241,0.2)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translate(-50%, -50%)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(99,102,241,0.5), 0 0 0 6px rgba(99,102,241,0.15)';
            }}
          >
            <span style={{ width: 0, height: 0, borderStyle: 'solid', borderWidth: '11px 0 11px 20px', borderColor: 'transparent transparent transparent #fff', marginLeft: 4, display: 'block' }} />
          </button>
          <HeroExecutionViz projects={hero.executionProjects} />
        </FadeUp>
      </div>

      {showVideo && (
        <div
          onClick={() => setShowVideo(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}
        >
          <div onClick={e => e.stopPropagation()} style={{ position: 'relative', width: '90%', maxWidth: 820, aspectRatio: '16/9', borderRadius: 12, overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.5)' }}>
            <button
              type="button"
              onClick={() => setShowVideo(false)}
              style={{ position: 'absolute', top: 12, right: 12, width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}
            >
              ✕
            </button>
            <video autoPlay controls style={{ width: '100%', height: '100%', objectFit: 'cover' }}>
              <source src="/Features To Offer (1).mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Workflow
// ---------------------------------------------------------------------------

const WF_DESKTOP_LAYOUT = [
  { id: 'requirements', col: 1, row: 1 },
  { id: 'architecture', col: 3, row: 1 },
  { id: 'frontend', col: 5, row: 1 },
  { id: 'deployment', col: 1, row: 3 },
  { id: 'testing', col: 3, row: 3 },
  { id: 'backend', col: 5, row: 3 },
];

const WF_TABLET_LAYOUT = [
  { id: 'requirements', col: 1, row: 1 },
  { id: 'architecture', col: 3, row: 1 },
  { id: 'frontend', col: 5, row: 1 },
  { id: 'backend', col: 1, row: 3 },
  { id: 'testing', col: 3, row: 3 },
  { id: 'deployment', col: 5, row: 3 },
];

const WF_DESKTOP_CONNECTIONS = [
  { from: 'requirements', to: 'architecture', type: 'right', col: 2, row: 1 },
  { from: 'architecture', to: 'frontend', type: 'right', col: 4, row: 1 },
  { from: 'frontend', to: 'backend', type: 'down', col: 5, row: 2 },
  { from: 'backend', to: 'testing', type: 'left', col: 4, row: 3 },
  { from: 'testing', to: 'deployment', type: 'left', col: 2, row: 3 },
];

const WF_TABLET_CONNECTIONS = [
  { from: 'requirements', to: 'architecture', type: 'right', col: 2, row: 1 },
  { from: 'architecture', to: 'frontend', type: 'right', col: 4, row: 1 },
  { from: 'frontend', to: 'backend', type: 'down', col: 5, row: 2 },
  { from: 'backend', to: 'testing', type: 'right', col: 2, row: 3 },
  { from: 'testing', to: 'deployment', type: 'right', col: 4, row: 3 },
];

function WfStageCard({ stage, active, onEnter, onLeave, style, compact }) {
  return (
    <article
      className={`lp-wf-node${active ? ' lp-wf-node--active' : ''}${compact ? ' lp-wf-node--compact' : ''}`}
      style={style}
      onMouseEnter={() => onEnter(stage.id)}
      onMouseLeave={onLeave}
      onFocus={() => onEnter(stage.id)}
      onBlur={onLeave}
      tabIndex={0}
    >
      <h3 className="lp-wf-node__title">{stage.title}</h3>
      <p className="lp-wf-node__copy">{stage.copy}</p>
    </article>
  );
}

function WfTimelineGrid({ layout, connections, stageMap, activeId, setActiveId, className }) {
  const isActive = (id) => activeId === id;
  const isConnLive = (from, to) => activeId === from || activeId === to;

  return (
    <div className={className}>
      {layout.map(({ id, col, row }) => (
        <WfStageCard
          key={id}
          stage={stageMap[id]}
          active={isActive(id)}
          onEnter={setActiveId}
          onLeave={() => setActiveId(null)}
          style={{ gridColumn: col, gridRow: row }}
          compact={className.includes('tablet')}
        />
      ))}
      {connections.map(conn => (
        <span
          key={`${conn.from}-${conn.to}`}
          className={[
            'lp-wf-conn',
            `lp-wf-conn--${conn.type}`,
            isConnLive(conn.from, conn.to) && 'lp-wf-conn--live',
          ].filter(Boolean).join(' ')}
          style={{ gridColumn: conn.col, gridRow: conn.row }}
          aria-hidden
        />
      ))}
    </div>
  );
}

function LandingWorkflow() {
  const { workflow } = C;
  const [activeId, setActiveId] = useState(null);
  const stageMap = Object.fromEntries(workflow.stages.map(s => [s.id, s]));

  return (
    <section id="workflow" className="lp-section lp-workflow-section">
      <FadeUp>
        <p className="lp-workflow__label">{workflow.label}</p>
        <h2 className="lp-workflow__title">{workflow.title}</h2>
        <p className="lp-workflow__intro">{workflow.intro}</p>

        <div className="lp-wf-timeline" aria-label="Software delivery workflow">
          <WfTimelineGrid
            layout={WF_DESKTOP_LAYOUT}
            connections={WF_DESKTOP_CONNECTIONS}
            stageMap={stageMap}
            activeId={activeId}
            setActiveId={setActiveId}
            className="lp-wf-timeline__desktop"
          />

          <WfTimelineGrid
            layout={WF_TABLET_LAYOUT}
            connections={WF_TABLET_CONNECTIONS}
            stageMap={stageMap}
            activeId={activeId}
            setActiveId={setActiveId}
            className="lp-wf-timeline__tablet"
          />

          <div className="lp-wf-timeline__mobile">
            {workflow.stages.map((stage, i) => (
              <React.Fragment key={stage.id}>
                <WfStageCard
                  stage={stage}
                  active={activeId === stage.id}
                  onEnter={setActiveId}
                  onLeave={() => setActiveId(null)}
                />
                {i < workflow.stages.length - 1 && (
                  <span className="lp-wf-conn lp-wf-conn--down lp-wf-conn--mobile" aria-hidden />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {workflow.capabilities.length > 0 && (
          <div className="lp-workflow__capabilities" role="list">
            {workflow.capabilities.map(item => (
              <span key={item} className="lp-workflow__cap" role="listitem">{item}</span>
            ))}
          </div>
        )}
      </FadeUp>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Agent platform — screenshot-matched mosaic
// ---------------------------------------------------------------------------

const CAP_STEP_MS = 2200;
const CAP_SHIP_MS = 2600;
const WS_TICK_MS = 3200;

const WS_METRIC_SETS = [
  { users: '1,248', revenue: '$48.2K', growth: '+18%' },
  { users: '1,286', revenue: '$49.1K', growth: '+19%' },
  { users: '1,312', revenue: '$51.4K', growth: '+21%' },
  { users: '1,348', revenue: '$52.8K', growth: '+22%' },
];

const WS_AI_REPLIES = [
  'Permissions module generated. Admin, Manager, and User roles added.',
  'Authentication flow updated.',
  'Analytics widget generated.',
  'Deployment configuration added.',
];

const WS_USER_MSG = 'Add role-based permissions.';

const WS_CODE_SNAPSHOTS = [
  {
    highlight: null,
    lines: [
      [[['kw', 'export default function '], ['fn', 'Dashboard'], ['p', '() {']]],
      [[['p', '  return (']]],
      [[['p', '    <'], ['tag', 'AnalyticsView']]],
      [[['p', '      users={'], ['num', '1248'], ['p', '}']]],
      [[['p', '      revenue="'], ['str', '$48.2k'], ['p', '"']]],
      [[['p', '    />']]],
    ],
  },
  {
    highlight: 3,
    lines: [
      [[['kw', 'export default function '], ['fn', 'Dashboard'], ['p', '() {']]],
      [[['p', '  return (']]],
      [[['p', '    <'], ['tag', 'AnalyticsView']]],
      [[['p', '      users={'], ['num', '1286'], ['p', '}']]],
      [[['p', '      revenue="'], ['str', '$48.2k'], ['p', '"']]],
      [[['p', '    />']]],
    ],
  },
  {
    highlight: 4,
    lines: [
      [[['kw', 'export default function '], ['fn', 'Dashboard'], ['p', '() {']]],
      [[['p', '  return (']]],
      [[['p', '    <'], ['tag', 'AnalyticsView']]],
      [[['p', '      users={'], ['num', '1286'], ['p', '}']]],
      [[['p', '      revenue="'], ['str', '$49.1k'], ['p', '"']]],
      [[['p', '    />']]],
    ],
  },
  {
    highlight: 2,
    lines: [
      [[['kw', 'export default function '], ['fn', 'Dashboard'], ['p', '() {']]],
      [[['p', '  return (']]],
      [[['p', '    <'], ['tag', 'DashboardView']]],
      [[['p', '      users={'], ['num', '1286'], ['p', '}']]],
      [[['p', '      revenue="'], ['str', '$49.1k'], ['p', '"']]],
      [[['p', '    />']]],
    ],
  },
];

function WsCodeLine({ tokens, active }) {
  return (
    <div className={`lp-cap__ws-code-line${active ? ' lp-cap__ws-code-line--active' : ''}`}>
      {tokens.map(([type, text], i) => (
        <span key={i} className={`lp-cap__ws-tok lp-cap__ws-tok--${type}`}>{text}</span>
      ))}
    </div>
  );
}

function CapWorkspaceMock({ syncStatus, inView }) {
  const reduce = useReducedMotion();
  const [pulse, setPulse] = useState(true);
  const [tick, setTick] = useState(0);

  const codeIdx = tick % WS_CODE_SNAPSHOTS.length;
  const metricIdx = tick % WS_METRIC_SETS.length;
  const replyIdx = tick % WS_AI_REPLIES.length;
  const snapshot = WS_CODE_SNAPSHOTS[codeIdx];
  const metrics = WS_METRIC_SETS[metricIdx];

  useEffect(() => {
    if (!inView || reduce) return undefined;
    const timer = setInterval(() => setPulse(p => !p), 2000);
    return () => clearInterval(timer);
  }, [inView, reduce]);

  useEffect(() => {
    if (!inView || reduce) return undefined;
    const timer = setInterval(() => setTick(t => t + 1), WS_TICK_MS);
    return () => clearInterval(timer);
  }, [inView, reduce]);

  return (
    <div className="lp-cap__ws-mock">
      <div className="lp-cap__ws-panels">
        <div className="lp-cap__ws-panel">
          <div className="lp-cap__ws-panel-head">
            <span className="lp-cap__ws-code-icon" aria-hidden>&lt;/&gt;</span>
            <span className="lp-cap__ws-panel-label">Code</span>
          </div>
          <div className="lp-cap__ws-panel-body lp-cap__ws-panel-body--code">
            <pre className="lp-cap__ws-code" aria-hidden>
              {snapshot.lines.map((lineTokens, i) => (
                <WsCodeLine
                  key={`${codeIdx}-${i}`}
                  tokens={lineTokens}
                  active={snapshot.highlight === i}
                />
              ))}
            </pre>
          </div>
        </div>
        <div className="lp-cap__ws-panel">
          <div className="lp-cap__ws-panel-head">
            <span className={`lp-cap__ws-live${pulse ? ' lp-cap__ws-live--on' : ''}`} aria-hidden />
            <span className="lp-cap__ws-panel-label">Preview</span>
          </div>
          <div className="lp-cap__ws-panel-body lp-cap__ws-panel-body--preview">
            <p className="lp-cap__ws-preview-title">Dashboard Overview</p>
            <div className="lp-cap__ws-metrics">
              {[
                { label: 'Users', key: 'users' },
                { label: 'Revenue', key: 'revenue' },
                { label: 'Growth', key: 'growth' },
              ].map(({ label, key }) => (
                <div key={label} className="lp-cap__ws-metric">
                  <span className="lp-cap__ws-metric-label">{label}</span>
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={metrics[key]}
                      className="lp-cap__ws-metric-val"
                      initial={reduce ? false : { opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={reduce ? undefined : { opacity: 0 }}
                      transition={{ duration: 0.35, ease: 'easeOut' }}
                    >
                      {metrics[key]}
                    </motion.span>
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="lp-cap__ws-panel">
          <div className="lp-cap__ws-panel-head">
            <span className={`lp-cap__ws-live${pulse ? ' lp-cap__ws-live--on' : ''}`} aria-hidden />
            <span className="lp-cap__ws-panel-label">Chat</span>
          </div>
          <div className="lp-cap__ws-panel-body lp-cap__ws-panel-body--chat">
            <div className="lp-cap__ws-msg lp-cap__ws-msg--user">
              <span className="lp-cap__ws-msg-who">You</span>
              <span className="lp-cap__ws-msg-text">{WS_USER_MSG}</span>
            </div>
            <div className="lp-cap__ws-msg lp-cap__ws-msg--ai">
              <span className="lp-cap__ws-msg-who">CodeXAI</span>
              <AnimatePresence mode="wait">
                <motion.span
                  key={replyIdx}
                  className="lp-cap__ws-msg-text"
                  initial={reduce ? false : { opacity: 0, y: 3 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduce ? undefined : { opacity: 0, y: -3 }}
                  transition={{ duration: 0.35, ease: 'easeOut' }}
                >
                  {WS_AI_REPLIES[replyIdx]}
                </motion.span>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
      <p className="lp-cap__card-foot">
        <span className="lp-cap__ws-sync-icon"><CapSyncIcon /></span>
        {syncStatus}
      </p>
    </div>
  );
}

function CapDescribeMock({ prompt, steps, inView }) {
  const reduce = useReducedMotion();
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    if (!inView || reduce) return undefined;
    const timer = setInterval(() => setStepIdx(i => (i + 1) % steps.length), CAP_STEP_MS);
    return () => clearInterval(timer);
  }, [inView, reduce, steps.length]);

  return (
    <div className="lp-cap__describe-mock">
      <div className="lp-cap__prompt-box">
        <span className="lp-cap__prompt-sparkle" aria-hidden>✦</span>
        <span className="lp-cap__prompt-text">{prompt}</span>
        <span className="lp-cap__prompt-send" aria-hidden>↑</span>
      </div>
      <ul className="lp-cap__stepper">
        {steps.map((step, i) => (
          <li
            key={step}
            className={`lp-cap__step${i === stepIdx ? ' lp-cap__step--active' : ''}${i < stepIdx ? ' lp-cap__step--done' : ''}`}
          >
            <span className="lp-cap__step-dot" aria-hidden />
            <span>{step}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CapParallelMock({ footer, inView }) {
  const reduce = useReducedMotion();
  const [feLines, setFeLines] = useState(1);
  const [beLines, setBeLines] = useState(1);

  useEffect(() => {
    if (!inView || reduce) return undefined;
    const fe = setInterval(() => setFeLines(n => (n >= 3 ? 1 : n + 1)), 1200);
    const be = setInterval(() => setBeLines(n => (n >= 3 ? 1 : n + 1)), 1500);
    return () => { clearInterval(fe); clearInterval(be); };
  }, [inView, reduce]);

  return (
    <div className="lp-cap__parallel-mock">
      <div className="lp-cap__mini-panels">
        <div className="lp-cap__mini-panel">
          <div className="lp-cap__mini-head">
            <span className="lp-cap__mini-icon" aria-hidden>&lt;/&gt;</span>
            <span>Frontend</span>
          </div>
          <div className="lp-cap__mini-body">
            {Array.from({ length: feLines }, (_, i) => (
              <span key={i} className={`lp-cap__mini-line lp-cap__mini-line--w${i + 1}`} />
            ))}
          </div>
        </div>
        <div className="lp-cap__mini-panel">
          <div className="lp-cap__mini-head">
            <span className="lp-cap__mini-icon" aria-hidden>⬡</span>
            <span>Backend</span>
          </div>
          <div className="lp-cap__mini-body">
            {Array.from({ length: beLines }, (_, i) => (
              <span key={i} className={`lp-cap__mini-line lp-cap__mini-line--w${(i % 3) + 1}`} />
            ))}
          </div>
        </div>
      </div>
      <p className="lp-cap__card-foot">
        <span className="lp-cap__foot-icon" aria-hidden>⚡</span>
        {footer}
      </p>
    </div>
  );
}

function CapShipMock({ stages, footer, inView }) {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!inView || reduce) return undefined;
    const timer = setInterval(() => setActive(i => (i + 1) % stages.length), CAP_SHIP_MS);
    return () => clearInterval(timer);
  }, [inView, reduce, stages.length]);

  return (
    <div className="lp-cap__ship-mock">
      <div className="lp-cap__ship-route">
        {stages.map((stage, i) => (
          <React.Fragment key={stage}>
            <span className={`lp-cap__ship-node${i === active ? ' lp-cap__ship-node--active' : ''}`}>
              {stage}
            </span>
            {i < stages.length - 1 && <span className="lp-cap__ship-arrow" aria-hidden>→</span>}
          </React.Fragment>
        ))}
      </div>
      <p className="lp-cap__card-foot">
        <span className="lp-cap__foot-dot" aria-hidden />
        {footer}
      </p>
    </div>
  );
}

function CapCardVisual({ card, inView }) {
  switch (card.id) {
    case 'describe':
      return <CapDescribeMock prompt={card.prompt} steps={card.steps} inView={inView} />;
    case 'parallel':
      return <CapParallelMock footer={card.footer} inView={inView} />;
    case 'ship':
      return <CapShipMock stages={card.stages} footer={card.footer} inView={inView} />;
    case 'workspace':
      return <CapWorkspaceMock syncStatus={card.syncStatus} inView={inView} />;
    default:
      return null;
  }
}

function CapStoryCard({ card, index, inView }) {
  const reduce = useReducedMotion();
  const Icon = CAP_CARD_ICONS[card.id];

  return (
    <motion.article
      className={`lp-cap__card lp-cap__card--${card.id}`}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
      whileHover={reduce ? {} : { y: -4, transition: { duration: 0.22 } }}
    >
      <div className="lp-cap__card-head">
        {Icon && (
          <span className="lp-cap__card-icon" aria-hidden>
            <Icon />
          </span>
        )}
        <div className="lp-cap__card-body">
          <h3 className="lp-cap__card-title">{card.title}</h3>
          <p className="lp-cap__card-copy">{card.description}</p>
        </div>
      </div>
      <CapCardVisual card={card} inView={inView} />
    </motion.article>
  );
}

function LandingFeatureStory() {
  const { featureStory } = C;
  const ref = useRef(null);
  const inView = useInView(ref, { once: false, margin: '-60px' });
  const cards = Object.fromEntries(featureStory.cards.map(c => [c.id, c]));

  return (
    <section id="how-it-works" className="lp-section lp-feature-story" ref={ref}>
      <div className="lp-cap">
        <FadeUp className="lp-cap__header">
          <div className="lp-cap__header-main">
            <p className="lp-cap__label">{featureStory.label}</p>
            <h2 className="lp-cap__title">{featureStory.title}</h2>
          </div>
          {featureStory.description ? (
            <p className="lp-cap__intro">{featureStory.description}</p>
          ) : null}
        </FadeUp>

        <div className="lp-cap__mosaic">
          <div className="lp-cap__cell lp-cap__cell--describe">
            <CapStoryCard card={cards.describe} index={0} inView={inView} />
          </div>
          <div className="lp-cap__cell lp-cap__cell--parallel">
            <CapStoryCard card={cards.parallel} index={1} inView={inView} />
          </div>
          <div className="lp-cap__cell lp-cap__cell--ship">
            <CapStoryCard card={cards.ship} index={2} inView={inView} />
          </div>
          <div className="lp-cap__cell lp-cap__cell--workspace">
            <CapStoryCard card={cards.workspace} index={3} inView={inView} />
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Platform bento showcase
// ---------------------------------------------------------------------------

const BENTO_TICK_MS = 1400;
const BENTO_ORCH_TICK_MS = 2200;

function BentoOrchTimeline({ timeline, inView }) {
  const reduce = useReducedMotion();
  const [head, setHead] = useState(0);

  useEffect(() => {
    if (!inView || reduce) return undefined;
    const timer = setInterval(() => setHead(h => (h + 1) % timeline.length), BENTO_ORCH_TICK_MS);
    return () => clearInterval(timer);
  }, [inView, reduce, timeline.length]);

  const windowSize = 5;
  const rows = Array.from({ length: windowSize }, (_, i) => {
    const idx = (head + i) % timeline.length;
    return { ...timeline[idx], pos: i };
  });

  return (
    <div className="lp-bento-timeline" aria-live="polite">
      {rows.map(({ time, event, pos }) => (
        <motion.div
          key={`${head}-${pos}`}
          className={`lp-bento-timeline__row${pos === 0 ? ' lp-bento-timeline__row--current' : ''}`}
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: Math.max(0.35, 1 - pos * 0.12), y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="lp-bento-timeline__time">{time}</span>
          <span className="lp-bento-timeline__event">{event}</span>
        </motion.div>
      ))}
    </div>
  );
}

function BentoWorkspaceEvents({ events, inView }) {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!inView || reduce) return undefined;
    const timer = setInterval(() => setActive(i => (i + 1) % events.length), BENTO_TICK_MS);
    return () => clearInterval(timer);
  }, [inView, reduce, events.length]);

  return (
    <ul className="lp-bento-ws-events" aria-live="polite">
      {events.map((label, i) => {
        const isActive = i === active;
        const isDone = i < active;
        return (
          <motion.li
            key={label}
            className={[
              'lp-bento-ws-events__item',
              isActive && 'lp-bento-ws-events__item--active',
              isDone && 'lp-bento-ws-events__item--done',
            ].filter(Boolean).join(' ')}
            layout
            transition={{ duration: 0.35 }}
          >
            <span className="lp-bento-ws-events__dot" aria-hidden />
            {label}
          </motion.li>
        );
      })}
    </ul>
  );
}

function BentoIntegrationsFeed({ events, inView }) {
  const reduce = useReducedMotion();
  const [head, setHead] = useState(0);

  useEffect(() => {
    if (!inView || reduce) return undefined;
    const timer = setInterval(() => setHead(h => (h + 1) % events.length), BENTO_TICK_MS);
    return () => clearInterval(timer);
  }, [inView, reduce, events.length]);

  const windowSize = 4;
  const rows = Array.from({ length: windowSize }, (_, i) => {
    const idx = (head + i) % events.length;
    return { text: events[idx], pos: i };
  });

  return (
    <div className="lp-bento-int-feed" aria-live="polite">
      {rows.map(({ text, pos }) => (
        <motion.div
          key={`${head}-${pos}`}
          className={`lp-bento-int-feed__row${pos === 0 ? ' lp-bento-int-feed__row--current' : ''}`}
          initial={reduce ? false : { opacity: 0, x: 24 }}
          animate={{ opacity: Math.max(0.3, 1 - pos * 0.15), x: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          {text}
        </motion.div>
      ))}
    </div>
  );
}

function BentoEnterpriseStates({ items, inView }) {
  const reduce = useReducedMotion();
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!inView || reduce) return undefined;
    const timer = setInterval(() => {
      setActiveIdx(i => (i + 1) % (items.length + 1));
    }, BENTO_TICK_MS);
    return () => clearInterval(timer);
  }, [inView, reduce, items.length]);

  return (
    <ul className="lp-bento-verify">
      {items.map((item, i) => {
        const verified = activeIdx > i || activeIdx >= items.length;
        const pending = activeIdx === i;
        const statusWord = item.split(' ').slice(-1)[0];
        const stateLabel = verified ? statusWord : pending ? 'Validating' : 'Pending';
        return (
          <motion.li
            key={item}
            className={[
              'lp-bento-verify__item',
              verified && 'lp-bento-verify__item--verified',
              pending && 'lp-bento-verify__item--pending',
            ].filter(Boolean).join(' ')}
            layout
            transition={{ duration: 0.4 }}
          >
            <span className="lp-bento-verify__label">{item}</span>
            <span className="lp-bento-verify__state">{stateLabel}</span>
          </motion.li>
        );
      })}
    </ul>
  );
}

function BentoCardVisual({ card, inView }) {
  switch (card.id) {
    case 'orchestration':
      return (
        <>
          <ul className="lp-bento__caps">
            {card.capabilities.map(cap => (
              <li key={cap}>{cap}</li>
            ))}
          </ul>
          <BentoOrchTimeline timeline={card.timeline} inView={inView} />
        </>
      );
    case 'workspace':
      return <BentoWorkspaceEvents events={card.events} inView={inView} />;
    case 'integrations':
      return <BentoIntegrationsFeed events={card.events} inView={inView} />;
    case 'enterprise':
      return <BentoEnterpriseStates items={card.verifications} inView={inView} />;
    default:
      return null;
  }
}

function BentoCard({ card, cellClass, inView }) {
  const reduce = useReducedMotion();
  return (
    <motion.article
      className={`lp-bento__card lp-bento__card--${card.id} ${cellClass}`}
      whileHover={reduce ? {} : { y: -4, transition: { duration: 0.25 } }}
    >
      <h3 className="lp-bento__card-title">{card.title}</h3>
      <p className="lp-bento__card-copy">{card.copy}</p>
      <div className="lp-bento__card-viz">
        <BentoCardVisual card={card} inView={inView} />
      </div>
    </motion.article>
  );
}

function LandingPlatform() {
  const { platform } = C;
  const ref = useRef(null);
  const inView = useInView(ref, { once: false, margin: '-80px' });
  const cards = Object.fromEntries(platform.cards.map(c => [c.id, c]));

  return (
    <section id="workspace" className="lp-section lp-platform-section" ref={ref}>
      <FadeUp className="lp-bento__header">
        <p className="lp-bento__label">{platform.label}</p>
        <h2 className="lp-bento__title">{platform.title}</h2>
        {platform.intro ? <p className="lp-bento__intro">{platform.intro}</p> : null}
      </FadeUp>

      <div className="lp-bento">
        <BentoCard card={cards.orchestration} cellClass="lp-bento__cell lp-bento__cell--orch" inView={inView} />
        <BentoCard card={cards.workspace} cellClass="lp-bento__cell lp-bento__cell--ws" inView={inView} />
        <BentoCard card={cards.enterprise} cellClass="lp-bento__cell lp-bento__cell--ent" inView={inView} />
        <BentoCard card={cards.integrations} cellClass="lp-bento__cell lp-bento__cell--int" inView={inView} />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Templates, pricing, CTA, footer
// ---------------------------------------------------------------------------

function LandingWhyCodexAI() {
  const { whyCodexAI } = C;
  return (
    <section className="lp-why">
      <div className="lp-container">
        <FadeUp className="lp-why__inner">
          <div className="lp-why__left">
            <p className="lp-why__label">{whyCodexAI.label}</p>
            <h2 className="lp-why__heading">
              {whyCodexAI.heading[0]}<br />
              <span className="lp-why__heading-accent">{whyCodexAI.heading[1]}</span>
            </h2>
          </div>
          <div className="lp-why__right">
            <p className="lp-why__body">{whyCodexAI.body}</p>
            <p className="lp-why__detail">{whyCodexAI.detail}</p>
            <div className="lp-why__pillars">
              {whyCodexAI.pillars.map(p => (
                <span key={p} className="lp-why__pillar">{p}</span>
              ))}
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function LandingBrandStory() { return null; }

function LandingTemplatesShowcase() {
  const { templates } = C;

  return (
    <section id="templates" className="lp-section">
      <FadeUp>
        <h2 className="lp-section__title lp-section__title--left">{templates.title}</h2>
        <p className="lp-section__sub lp-section__sub--left">{templates.subtitle}</p>
        <div className="lp-templates">
          {templates.items.map(t => (
            <article key={t.name} className="lp-template">
              <div className="lp-template__frame">
                <img src={t.image} alt="" className="lp-template__img" loading="lazy" />
                <div className="lp-template__overlay">
                  <span className="lp-template__preview-label">Preview</span>
                </div>
              </div>
              <h3 className="lp-template__name">{t.name}</h3>
            </article>
          ))}
        </div>
        <div className="lp-section__cta">
          <LoginLink className="lp-btn lp-btn--secondary">{templates.cta}</LoginLink>
        </div>
      </FadeUp>
    </section>
  );
}

function LandingBlogPreview() {
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/blog?page=1&page_size=3`)
      .then((r) => r.json())
      .then((data) => setPosts(data.posts || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  if (loaded && posts.length === 0) return null;

  return (
    <section id="blog" className="lp-section lp-blog">
      <style>{BLOG_PREVIEW_CSS}</style>
      <FadeUp>
        <h2 className="lp-section__title">Insights and updates</h2>
      </FadeUp>

      <div className="lp-blog__grid">
        {posts.map((post, i) => (
          <FadeUp key={post.id} delay={i * 0.08} className="lp-blog__card-wrap">
            <Link to={`/blog/${post.slug}`} className="lp-blog__card">
              {post.cover_image_url && (
                <div className="lp-blog__card-image" style={{ backgroundImage: `url(${post.cover_image_url})` }} />
              )}
              <div className="lp-blog__card-body">
                <h3 className="lp-blog__card-title">{post.title}</h3>
                {post.excerpt && <p className="lp-blog__card-excerpt">{post.excerpt}</p>}
              </div>
            </Link>
          </FadeUp>
        ))}
      </div>

      <FadeUp className="lp-blog__cta-wrap">
        <Link to="/blog" className="lp-blog__cta">View All Posts →</Link>
      </FadeUp>
    </section>
  );
}

const BLOG_PREVIEW_CSS = `
.lp-blog__grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 24px; margin-top: 8px; }
.lp-blog__card-wrap { display: block; }
.lp-blog__card {
  display: block; height: 100%; text-decoration: none; color: inherit; border-radius: 16px; overflow: hidden;
  background: rgba(30,41,59,0.5); border: 1px solid rgba(255,255,255,0.08); transition: transform 0.2s, box-shadow 0.2s;
}
[data-theme="light"] .lp-blog__card { background: #fff; border-color: rgba(99,102,241,0.1); box-shadow: 0 2px 10px rgba(0,0,0,0.05); }
.lp-blog__card:hover { transform: translateY(-4px); box-shadow: 0 16px 32px rgba(0,0,0,0.22); }
.lp-blog__card-image { width: 100%; height: 160px; background-size: cover; background-position: center; }
.lp-blog__card-body { padding: 20px 22px; }
.lp-blog__card-title { font-size: 17px; font-weight: 700; margin: 0 0 8px; line-height: 1.35; }
.lp-blog__card-excerpt { font-size: 13px; color: var(--text-muted, #94a3b8); margin: 0; line-height: 1.55; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.lp-blog__cta-wrap { text-align: center; margin-top: 40px; }
.lp-blog__cta { display: inline-block; font-size: 14px; font-weight: 700; color: #a5b4fc; text-decoration: none; }
.lp-blog__cta:hover { text-decoration: underline; }
`;

function LandingPricing() {
  const { pricing } = C;

  return (
    <section id="pricing" className="lp-section">
      <FadeUp>
        <h2 className="lp-section__title">{pricing.title}</h2>
        <div className="lp-pricing">
          {pricing.tiers.map(tier => (
            <article
              key={tier.id}
              className={[
                'lp-pricing__card',
                tier.recommended && 'lp-pricing__card--featured',
                tier.enterprise && 'lp-pricing__card--enterprise',
              ].filter(Boolean).join(' ')}
            >
              {tier.recommended && <span className="lp-pricing__badge">Recommended</span>}
              <p className="lp-pricing__name">{tier.name}</p>
              {tier.description && <p className="lp-pricing__period">{tier.description}</p>}
              <p className="lp-pricing__price">
                {tier.price}
                {tier.period && <span className="lp-pricing__period">{tier.period}</span>}
              </p>
              <ul className="lp-pricing__features">
                {tier.features.map(f => <li key={f}>{f}</li>)}
              </ul>
              {tier.id === 'starter' ? (
                <GetStartedLink
                  className={`lp-btn ${tier.recommended ? 'lp-btn--primary' : 'lp-btn--secondary'}`}
                >
                  {tier.cta}
                </GetStartedLink>
              ) : (
                <LoginLink
                  className={`lp-btn ${tier.recommended ? 'lp-btn--primary' : 'lp-btn--secondary'}`}
                >
                  {tier.cta}
                </LoginLink>
              )}
            </article>
          ))}
        </div>
      </FadeUp>
    </section>
  );
}

function LandingFinalCTA() {
  const { finalCta } = C;

  return (
    <section className="lp-final">
      <div className="lp-final__glow" aria-hidden />
      <FadeUp className="lp-final__inner">
        <h2 className="lp-final__title">{finalCta.headline}</h2>
        <p className="lp-final__sub">{finalCta.subheadline}</p>
        <GetStartedLink className="lp-btn lp-btn--primary lp-btn--lg">{finalCta.cta}</GetStartedLink>
      </FadeUp>
    </section>
  );
}

function LandingFooter({ onScrollTo }) {
  const { footer } = C;

  const socials = [
    { label: 'Facebook', id: 'facebook', href: 'https://www.facebook.com/devstringx', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
    { label: 'X', id: 'x', href: 'https://x.com/devstringx_tech', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
    { label: 'Instagram', id: 'instagram', href: 'https://www.instagram.com/devstringx', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg> },
    { label: 'LinkedIn', id: 'linkedin', href: 'https://www.linkedin.com/company/devstringx-technologies-pvt-ltd/', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> },
  ];

  return (
    <footer className="lp-footer lp-footer--enhanced">
      <div className="lp-footer__grid">
        <div className="lp-footer__brand">
          <p className="lp-footer__name">{C.brand.name}</p>
          {footer.tagline ? <p className="lp-footer__tag">{footer.tagline}</p> : null}
        </div>
        <div>
          <p className="lp-footer__col-title">
            <svg className="lp-footer__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
            Our Address
          </p>
          <ul className="lp-footer__links">
            <li>13140 Coit Road, Dallas, TX 75240, USA</li>
            <li>Magnus Tower, Sector 73, Noida, UP 201307</li>
            <li>HSR Layout, Bengaluru, KA 560102</li>
          </ul>
        </div>
        <div>
          <p className="lp-footer__col-title">
            <svg className="lp-footer__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
            Contact Info
          </p>
          <ul className="lp-footer__links">
            <li><a href="tel:+19452122550">+1 (945) 212-2550</a></li>
            <li><a href="tel:+919818147186">+91 981 814 7186</a></li>
            <li><a href="mailto:info@devstringx.com">info@devstringx.com</a></li>
          </ul>
        </div>
        <div>
          <p className="lp-footer__col-title">
            <svg className="lp-footer__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            Certifications
          </p>
          <ul className="lp-footer__links">
            <li>ISO 27001 Certified</li>
            <li>SOC 2 Compliant</li>
            <li>NASSCOM Member</li>
            <li>AI Innovation Certified</li>
          </ul>
        </div>
      </div>
      <div className="lp-footer__bottom">
        <span className="lp-footer__powered">{footer.bottomLine || `${C.brand.name} — Powered by Devstringx`}</span>
        <span className="lp-footer__copyright">© {new Date().getFullYear()} Devstringx. All Rights Reserved.</span>
        <div className="lp-footer__socials">
          {socials.map(s => (
            <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className={`lp-footer__social lp-footer__social--${s.id}`} aria-label={s.label}>
              {s.icon}
            </a>
          ))}
        </div>
      </div>
    </footer>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function LandingPage() {
  const navigate = useNavigate();
  const scrollTo = (id) => {
    if (id === 'blog') {
      navigate('/blog');
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // CHAT-WIDGET-LANDING-ONLY-FEATURE: this widget must only ever show on
  // the public landing page ("/"), never inside the logged-in app — so it
  // can't live in public/index.html (that loads on every route, since this
  // is a single-page app). Injected on mount here instead, and removed on
  // unmount so navigating to /app or /blog doesn't carry it along.
  useEffect(() => {
    window.ChatHR = { siteId: 'gdqiat0x', serverUrl: 'https://dsxomnichat.com' };
    const script = document.createElement('script');
    script.src = 'https://dsxomnichat.com/widget/chat-widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Prefer the widget's own runtime API (it exposes window.WebChat with
      // hide()/destroy()) over guessing at its DOM nodes — cleanest teardown
      // when the user navigates from "/" into the logged-in app.
      try {
        if (window.WebChat?.destroy) window.WebChat.destroy();
        else if (window.WebChat?.hide) window.WebChat.hide();
      } catch (e) { /* widget not fully loaded yet — nothing to tear down */ }
      script.remove();
      delete window.ChatHR;
    };
  }, []);

  useEffect(() => {
    const { seo } = C;
    document.title = seo.title;

    const setMeta = (name, content, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', seo.description);
    setMeta('og:title', seo.title, true);
    setMeta('og:description', seo.description, true);
    setMeta('og:type', 'website', true);
    setMeta('og:url', seo.url, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', seo.title);
    setMeta('twitter:description', seo.description);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = seo.url;

    let jsonLd = document.getElementById('lp-jsonld');
    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.id = 'lp-jsonld';
      jsonLd.type = 'application/ld+json';
      document.head.appendChild(jsonLd);
    }
    jsonLd.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: C.brand.name,
      applicationCategory: 'DeveloperApplication',
      description: seo.description,
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
  }, []);

  return (
    <div className="lp-page">
      <div className="lp-ambient" aria-hidden>
        <div className="lp-ambient__orb lp-ambient__orb--1" />
        <div className="lp-ambient__orb lp-ambient__orb--2" />
      </div>

      <LandingNav onScrollTo={scrollTo} />

      <main className="lp-main">
        <LandingHero onSeeHowItWorks={() => scrollTo('how-it-works')} />

        <section className="lp-trusted" aria-label="Trusted by">
          <p className="lp-trusted__text">Trusted by growing businesses and digital transformation teams</p>
          <div className="lp-trusted__logos" aria-hidden="true">
            <div className="lp-trusted__track">
              {[0, 1].map((half) => (
                <div className="lp-trusted__group" key={half}>
                  {[0, 1, 2].flatMap(() => TRUSTED_LOGOS).map((logo, idx) => (
                    <img key={`${half}-${idx}`} src={logo.src} alt="" className="lp-trusted__logo-img" style={{ height: `${logo.h}px` }} />
                  ))}
                </div>
              ))}
            </div>
          </div>
          <span className="lp-sr-only">Trusted by {TRUSTED_LOGOS.map((l) => l.name).join(' and ')}</span>
        </section>

        <LandingFeatureStory />
        <LandingWorkflow />
        <LandingPlatform />
        <LandingTemplatesShowcase />
        <LandingBlogPreview />
        <LandingPricing />
        <LandingFinalCTA />
      </main>

      <LandingFooter onScrollTo={scrollTo} />
    </div>
  );
}
